// Currency.js

export const DEFAULT_CURRENCY_CODE = 'USD';

/** Currencies that store whole units (no minor subdivision) in Appdeck. */
const ZERO_DECIMAL_CURRENCIES = new Set(['BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF']);

/**
 * Normalizes a raw currency code to uppercase ISO 4217, or the default.
 *
 * @param {*} raw - Candidate currency code.
 * @returns {string} Uppercase currency code.
 */
export const normalizeCurrencyCode = (raw) => {
    const trimmed = String(raw ?? '').trim().toUpperCase();
    if (/^[A-Z]{3}$/u.test(trimmed)) return trimmed;
    return DEFAULT_CURRENCY_CODE;
};

/**
 * Resolves the currency code from a schema field definition.
 *
 * @param {object|null|undefined} field - Schema field.
 * @returns {string} Uppercase currency code.
 */
export const resolveFieldCurrencyCode = (field) => (
    normalizeCurrencyCode(field?.currencyCode || field?.currency || DEFAULT_CURRENCY_CODE)
);

/**
 * Returns the number of fraction digits for a currency (0 for yen-like codes).
 *
 * @param {string} [currencyCode=DEFAULT_CURRENCY_CODE] - ISO 4217 code.
 * @returns {number} Fraction digit count.
 */
export const getCurrencyFractionDigits = (currencyCode = DEFAULT_CURRENCY_CODE) => {
    const code = normalizeCurrencyCode(currencyCode);
    try {
        const digits = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: code,
        }).resolvedOptions().maximumFractionDigits;
        if (Number.isFinite(digits)) return digits;
    } catch {
        // Fall through to the zero-decimal set.
    }
    return ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 2;
};

// Format price nicely (expect minor units as input)
export const formatPrice = (amount, currency = DEFAULT_CURRENCY_CODE) => {
    if (amount == null) return '';

    const code = normalizeCurrencyCode(currency);
    const fractionDigits = getCurrencyFractionDigits(code);

    // Stripe-style: prices are stored in minor units → convert for display
    let normalized = amount;
    if (fractionDigits > 0) {
        normalized = amount / (10 ** fractionDigits);
    }

    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: code,
        }).format(normalized);
    } catch {
        return `${normalized} ${code}`;
    }
};

// Parse an unknown stored price into minor units (cents for USD-like currencies).
//
// Intended for *consumers* of persisted data (displays, etc.).
// Rules:
// - Numbers: integers are treated as minor units; non-integers are treated as legacy major units.
// - Strings:
//   - Digits-only strings (e.g. "5500") are treated as minor units.
//   - Decimal strings (e.g. "55.00") are treated as major units.
//   - Best-effort stripping of currency codes/symbols (e.g. "$55", "USD 55.00").
export const parsePriceCents = (raw, currencyCode = DEFAULT_CURRENCY_CODE) => {
    const fractionDigits = getCurrencyFractionDigits(currencyCode);
    const scale = 10 ** fractionDigits;

    if (raw === null || raw === undefined || raw === '') return 0;

    if (typeof raw === 'number') {
        if (!Number.isFinite(raw)) return 0;
        return Number.isInteger(raw) ? raw : Math.round(raw * scale);
    }

    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) return 0;

        let working = trimmed.replace(/,/g, '').trim();
        working = working.replace(/^[A-Za-z]{3}\s*/u, '').trim();
        working = working.replace(/^[^\d]+/u, '').replace(/[^\d.]+$/u, '');

        if (/^\d+$/u.test(working)) {
            const cents = parseInt(working, 10);
            return Number.isFinite(cents) ? cents : 0;
        }

        if (fractionDigits === 0) {
            const whole = parseFloat(working);
            return Number.isFinite(whole) ? Math.round(whole) : 0;
        }

        const decimalPattern = new RegExp(`^\\d+(\\.\\d{1,${fractionDigits}})?$`, 'u');
        if (decimalPattern.test(working)) {
            const major = parseFloat(working);
            return Number.isFinite(major) ? Math.round(major * scale) : 0;
        }
    }

    return 0;
};

const buildCurrencyInputPatterns = (fractionDigits) => {
    if (fractionDigits <= 0) {
        return {
            partial: /^\d[\d,]*$/u,
            allowed: /^\d[\d,]*$/u,
            strict: /^\d+$/u,
        };
    }

    return {
        partial: new RegExp(`^\\d[\\d,]*(\\.\\d{0,${fractionDigits}})?$`, 'u'),
        allowed: new RegExp(`^\\d[\\d,]*(\\.\\d{1,${fractionDigits}})?$`, 'u'),
        strict: new RegExp(`^\\d+(\\.\\d{1,${fractionDigits}})?$`, 'u'),
    };
};

export const isCurrencyInputPartial = (input, currencyCode = DEFAULT_CURRENCY_CODE) => {
    if (input === '') return true;
    const candidate = String(input ?? '');
    const { partial } = buildCurrencyInputPatterns(getCurrencyFractionDigits(currencyCode));
    return partial.test(candidate);
};

export const parseCurrencyInputToCents = (input, currencyCode = DEFAULT_CURRENCY_CODE) => {
    const trimmed = String(input ?? '').trim();
    if (trimmed === '') return { ok: true, cents: '' };

    const fractionDigits = getCurrencyFractionDigits(currencyCode);
    const scale = 10 ** fractionDigits;
    const { allowed, strict } = buildCurrencyInputPatterns(fractionDigits);

    if (!allowed.test(trimmed)) return { ok: false, cents: '' };

    const working = trimmed.replace(/,/g, '').trim();
    if (!strict.test(working)) return { ok: false, cents: '' };

    const major = parseFloat(working);
    if (!Number.isFinite(major)) return { ok: false, cents: '' };
    return { ok: true, cents: Math.round(major * (fractionDigits > 0 ? scale : 1)) };
};

/**
 * Formats a stored minor-unit value as an editable major-unit string.
 *
 * @param {*} raw - Stored currency value.
 * @param {string} [currencyCode=DEFAULT_CURRENCY_CODE] - ISO 4217 code.
 * @returns {string} Editable amount string.
 */
export const formatCurrencyDollarsString = (raw, currencyCode = DEFAULT_CURRENCY_CODE) => {
    if (raw === null || raw === undefined || raw === '') return '';
    const fractionDigits = getCurrencyFractionDigits(currencyCode);
    const cents = parsePriceCents(raw, currencyCode);
    if (!Number.isFinite(cents)) return '';
    if (fractionDigits <= 0) return String(cents);
    const scale = 10 ** fractionDigits;
    return (cents / scale).toFixed(fractionDigits);
};

/** Common ISO 4217 codes offered in currency pickers (schema settings + currency fields). */
export const CURRENCY_CODE_OPTIONS = Object.freeze([
    { value: 'USD', label: 'USD — US Dollar' },
    { value: 'EUR', label: 'EUR — Euro' },
    { value: 'GBP', label: 'GBP — British Pound' },
    { value: 'CAD', label: 'CAD — Canadian Dollar' },
    { value: 'AUD', label: 'AUD — Australian Dollar' },
    { value: 'JPY', label: 'JPY — Japanese Yen' },
    { value: 'CHF', label: 'CHF — Swiss Franc' },
    { value: 'CNY', label: 'CNY — Chinese Yuan' },
    { value: 'INR', label: 'INR — Indian Rupee' },
    { value: 'MXN', label: 'MXN — Mexican Peso' },
    { value: 'BRL', label: 'BRL — Brazilian Real' },
    { value: 'KRW', label: 'KRW — South Korean Won' },
    { value: 'SEK', label: 'SEK — Swedish Krona' },
    { value: 'NOK', label: 'NOK — Norwegian Krone' },
    { value: 'DKK', label: 'DKK — Danish Krone' },
    { value: 'NZD', label: 'NZD — New Zealand Dollar' },
    { value: 'SGD', label: 'SGD — Singapore Dollar' },
    { value: 'HKD', label: 'HKD — Hong Kong Dollar' },
    { value: 'PLN', label: 'PLN — Polish Zloty' },
    { value: 'ZAR', label: 'ZAR — South African Rand' },
]);

/**
 * Resolves a display label for an ISO currency code.
 *
 * @param {string} code - ISO 4217 code.
 * @returns {string} Label from CURRENCY_CODE_OPTIONS, or the code itself.
 */
export const getCurrencyCodeLabel = (code) => {
    const normalized = normalizeCurrencyCode(code);
    const match = CURRENCY_CODE_OPTIONS.find((option) => option.value === normalized);
    return match?.label || normalized;
};

/**
 * Normalizes a currency-code field value for storage (empty allowed).
 *
 * @param {*} raw - Candidate value.
 * @returns {string} Uppercase ISO code, or '' when empty/invalid.
 */
export const normalizeCurrencyCodeFieldValue = (raw) => {
    if (raw === null || raw === undefined || raw === '') return '';
    const trimmed = String(raw).trim().toUpperCase();
    if (!/^[A-Z]{3}$/u.test(trimmed)) return '';
    return trimmed;
};
